import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Package, ArrowRight, Filter } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { parcelsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatPrice, getStatusLabel, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';

const MyMissionsPage = () => {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchMissions = useCallback(async () => {
    try {
      const response = await parcelsApi.getAll();
      const myMissions = response.data.filter(p => p.carrier_id === user.id);
      setMissions(myMissions);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const filteredMissions = missions.filter(mission => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['accepted', 'in_progress'].includes(mission.status);
    if (filter === 'completed') return ['delivered', 'paid'].includes(mission.status);
    return mission.status === filter;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-missions-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
            Mes missions
          </h1>
          <p className="text-slate-600 mt-1">Gérez vos livraisons en cours et terminées</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]" data-testid="filter-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="active">En cours</SelectItem>
            <SelectItem value="completed">Terminées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="spinner mx-auto"></div>
          <p className="text-slate-500 mt-4">Chargement...</p>
        </div>
      ) : filteredMissions.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">Aucune mission trouvée</p>
            <Link to="/dashboard/available" className="text-orange-500 hover:text-orange-600 font-medium mt-2 inline-block">
              Voir les missions disponibles
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredMissions.map((mission) => (
            <Link 
              key={mission.id} 
              to={`/dashboard/parcel/${mission.id}`}
              className="block"
              data-testid={`mission-${mission.id}`}
            >
              <Card className="border-slate-200 hover:shadow-md hover:border-orange-200 transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      ['delivered', 'paid'].includes(mission.status) 
                        ? 'bg-green-100' 
                        : 'bg-yellow-100'
                    }`}>
                      <Truck className={`w-6 h-6 ${
                        ['delivered', 'paid'].includes(mission.status) 
                          ? 'text-green-600' 
                          : 'text-yellow-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">
                        {mission.pickup_address.city} → {mission.delivery_address.city}
                      </p>
                      <p className="text-sm text-slate-500">
                        {mission.weight}kg • {mission.distance_km}km • {formatDate(mission.pickup_date)}
                      </p>
                      <p className="text-sm text-slate-500">
                        Expéditeur: {mission.sender_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(mission.status)}`}>
                        {getStatusLabel(mission.status)}
                      </span>
                      <p className="text-lg font-bold text-green-600 mt-2">
                        {formatPrice(mission.proposed_price || mission.base_price)}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyMissionsPage;
